import { useLazyContext } from "@redotech/react-util/context";
import Edit04Icon from "@redotech/redo-icons/edit-04.svg";
import { MacroStatusToSet, SnoozeDuration } from "@redotech/redo-model/macro";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import {
  RedoBadge,
  RedoBadgeColor,
  RedoBadgeType,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import AsteriskIcon from "@redotech/redo-web/icon-old/asterisk.svg";
import FolderCheckIcon from "@redotech/redo-web/icon-old/folder-check.svg";
import SnoozeClockIcon from "@redotech/redo-web/icon-old/snooze-clock.svg";
import TagIcon from "@redotech/redo-web/icon-old/tag.svg";
import XIcon from "@redotech/redo-web/icon-old/x.svg";
import capitalize from "lodash/capitalize";
import { JSXElementConstructor, memo } from "react";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
} from "../conversation-tags/conversation-tag-pill";
import { ConversationTagsContext } from "../services/conversation-tags-service";

export const TagMacroAutomationPill = memo(function TagMacroAutomationPill({
  tag,
  xClicked,
}: {
  tag: string;
  xClicked?(): void;
}) {
  const [conversationTags] = useLazyContext(ConversationTagsContext);

  const teamTag = conversationTags.value?.find(
    (teamTag) => teamTag.name === tag,
  );
  const tagName = teamTag?.name || tag;
  const pillTheme = teamTag?.pillTheme
    ? teamTag.pillTheme
    : PillTheme.PRIMARY_LIGHT;
  return (
    <MacroAutomationPill
      color={BadgePillThemeToColorMapping[pillTheme]}
      Icon={TagIcon}
      label={tagName}
      type={BadgePillThemeToTypeMapping[pillTheme]}
      xClicked={xClicked}
    />
  );
});

export const StatusMacroAutomationPill = memo(
  function StatusMacroAutomationPill({
    statusToSet,
    snoozeDuration,
    xClicked,
  }: {
    statusToSet: MacroStatusToSet;
    snoozeDuration?: SnoozeDuration;
    xClicked?(): void;
  }) {
    const statusPillTheme = snoozeDuration
      ? PillTheme.NEUTRAL_YELLOW
      : statusToSet === "open"
        ? PillTheme.PRIMARY_LIGHT
        : PillTheme.NORMAL;
    return (
      <MacroAutomationPill
        color={BadgePillThemeToColorMapping[statusPillTheme]}
        Icon={
          snoozeDuration
            ? SnoozeClockIcon
            : statusToSet === "open"
              ? AsteriskIcon
              : FolderCheckIcon
        }
        label={`Set status: ${capitalize(statusToSet)}`}
        type={BadgePillThemeToTypeMapping[statusPillTheme]}
        xClicked={xClicked}
      />
    );
  },
);

export const EmailSubjectMacroAutomationPill = memo(
  function EmailSubjectMacroAutomationPill({
    emailSubject,
    xClicked,
  }: {
    emailSubject: string;
    xClicked?(): void;
  }) {
    return (
      <MacroAutomationPill
        color={BadgePillThemeToColorMapping[PillTheme.NEUTRAL_BLUE]}
        Icon={Edit04Icon}
        label={`Set email subject: ${emailSubject}`}
        type={BadgePillThemeToTypeMapping[PillTheme.NEUTRAL_BLUE]}
        xClicked={xClicked}
      />
    );
  },
);

export const MacroAutomationPill = memo(function MacroAutomationPill({
  color,
  label,
  Icon,
  type,
  xClicked,
}: {
  color: RedoBadgeColor;
  label: string;
  Icon: JSXElementConstructor<unknown>;
  type?: RedoBadgeType;
  xClicked?(): void;
}) {
  return (
    <RedoBadge
      color={color}
      iconTrailing={
        xClicked ? { type: "icon", Icon: XIcon, onClick: xClicked } : undefined
      }
      segmentLeading={{ type: "icon", Icon }}
      text={label}
      type={type}
    />
  );
});
